"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader, CircleCheck, CircleAlert, Calendar, Building2, Phone, Mail, User } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="121403" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-sm bg-primary/20 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold text-foreground font-default">Request Received</h3>
        <p className="text-muted-foreground max-w-md mx-auto font-default">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-sm">
          Submit Another Request
        </Button>
      </div>;
  }
  const selectClasses = "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-sm border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm appearance-none";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm flex items-start gap-3">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive-foreground font-default">{submitError}</p>
        </div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="flex items-center gap-2 font-default">
                <User className="size-4 text-primary" />
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="John Smith" className="rounded-sm" {...field} />
              </FormControl>
              <FormDescription className="font-default">Enter your full name for the project estimate.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="flex items-center gap-2 font-default">
                <Phone className="size-4 text-primary" />
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(925) 529-5005" className="rounded-sm" {...field} />
              </FormControl>
              <FormDescription className="font-default">Best contact number for our deck specialists.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="flex items-center gap-2 font-default">
                <Mail className="size-4 text-primary" />
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="info@pchbuilders.com" className="rounded-sm" {...field} />
              </FormControl>
              <FormDescription className="font-default">We'll send your formal quote to this address.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="property_type" rules={{
        required: "Please select a service type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="flex items-center gap-2 font-default">
                <Building2 className="size-4 text-primary" />
                Service Type
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <select className={selectClasses} {...field}>
                    <option value="" disabled>Select service type</option>
                    <option value="Composite & Trex">Composite & Trex Decks</option>
                    <option value="Pool Deck">Pool Deck Installation</option>
                    <option value="Wood Deck">Premium Wood Decks</option>
                    <option value="Structural">Structural & Concrete</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                    <svg className="size-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormDescription className="font-default">Choose the primary decking solution you need.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="preferred_date" render={({
      field
    }) => <FormItem>
            <FormLabel className="flex items-center gap-2 font-default">
              <SafeHydrate><Calendar className="size-4 text-primary" /></SafeHydrate>
              Desired Start Date
            </FormLabel>
            <FormControl>
              <Input type="date" {...field} className="w-full md:w-1/2 rounded-sm" />
            </FormControl>
            <FormDescription className="font-default">Select your target timeframe for construction.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default">Project Details & Zip Code</FormLabel>
            <FormControl>
              <Textarea placeholder="Please describe your project goals and include your service area zip code (e.g., San Francisco, Santa Clara, Santa Cruz, or Monterey)." className="min-h-[120px] resize-y rounded-sm" {...field} />
            </FormControl>
            <FormDescription className="font-default">Help us prepare for your site visit with specific requirements.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-lg font-bold bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-md hover:shadow-lg rounded-sm font-default">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending Request...
          </> : 'Schedule Free Site Visit'}
      </Button>
    </Form>;
}
export default function SecureYourExpertConsultation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="request-your-free-site-visit" className="dark bg-background text-foreground py-24 px-4 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-secondary/10 blur-[120px]" />
      </div>

      <div className="container mx-auto relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 tracking-tight font-default">
            Ready to Transform Your Backyard?
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default">
            Take the first step toward a premium outdoor living space. Connect with PCH Builders, California's trusted deck contractors since 1990, for a professional site assessment.
          </p>
        </div>

        <div ref={ref} className={`max-w-[800px] mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <Card className="bg-card/80 backdrop-blur-sm border-x-0 border-b-0 border-t-4 border-t-primary shadow-inner p-6 md:p-10 lg:p-12 rounded-sm">
            <FormProvider formKey="site_visit_form_home" formId="site_visit_form_home" sectionName="request-your-free-site-visit" defaultValues={{
            name: "",
            email: "",
            phone: "",
            property_type: "",
            preferred_date: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              minLength: 10
            },
            property_type: {
              required: true
            }
          }} submitText="Schedule Free Site Visit" successMessage="Thank you for choosing PCH Builders! Your request has been received. One of our deck building experts will contact you within 24 hours to confirm your free site visit.">
              <ConsultationForm />
            </FormProvider>
          </Card>
        </div>
      </div>
    </section>;
}